/**
 * Magic-byte ("file signature") sniffing for uploads.
 *
 * The server validates uploads twice (NCCCO.Common/Helpers/BlobStorageHelper.cs):
 *   1. the filename extension must be in AllowedExtensions, and
 *   2. the file's real leading bytes must match that extension.
 *
 * Gate 2 is what silently rejected mislabelled images — a WebP/HEIC/JPEG saved or
 * renamed as `.png` passes the browser's `accept` filter (which only looks at the
 * extension) but fails on the server with "File format 'png' not supported."
 * Because uploads happen on the final submit, the user only found out AFTER paying.
 *
 * Sniffing the first bytes at selection time lets us fail immediately, with a
 * message that names the real format. This is a UX guard only — the server check
 * stays as the security boundary, since anything client-side is bypassable.
 */

/** Formats the API accepts as upload content. */
export type SupportedFileType = 'jpg' | 'png' | 'pdf';

/** Formats we can name in an error message even though they're rejected. */
export type DetectedFileType = SupportedFileType | 'webp' | 'gif' | 'bmp' | 'heic' | 'avif' | 'tiff' | 'svg';

/** Extensions the server's AllowedExtensions permits for these upload fields. */
export const ALLOWED_UPLOAD_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];

const SUPPORTED: SupportedFileType[] = ['jpg', 'png', 'pdf'];

const startsWith = (bytes: Uint8Array, signature: number[], offset = 0) =>
    signature.every((value, i) => bytes[offset + i] === value);

/** Reads the first `length` bytes, falling back to FileReader on older browsers. */
const readHead = (file: File, length: number): Promise<Uint8Array> => {
    const blob = file.slice(0, length);

    if ('function' === typeof blob.arrayBuffer) {
        return blob.arrayBuffer().then(buffer => new Uint8Array(buffer));
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
    });
};

/**
 * Identifies a file by its leading bytes, ignoring the filename entirely.
 * Returns null when the format isn't one we recognise.
 */
export const sniffFileType = async (file: File): Promise<DetectedFileType | null> => {
    const b = await readHead(file, 16);

    if (startsWith(b, [0xff, 0xd8, 0xff])) return 'jpg';
    if (startsWith(b, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'png';
    if (startsWith(b, [0x25, 0x50, 0x44, 0x46])) return 'pdf';

    // RIFF....WEBP — the format Chrome's "Save image as" most often produces.
    if (startsWith(b, [0x52, 0x49, 0x46, 0x46]) && startsWith(b, [0x57, 0x45, 0x42, 0x50], 8)) return 'webp';

    // ISO-BMFF: "ftyp" at offset 4, brand at offset 8 (phone camera formats).
    if (startsWith(b, [0x66, 0x74, 0x79, 0x70], 4)) {
        if (startsWith(b, [0x68, 0x65, 0x69], 8) || startsWith(b, [0x6d, 0x69, 0x66, 0x31], 8)) return 'heic';
        if (startsWith(b, [0x61, 0x76, 0x69, 0x66], 8)) return 'avif';
    }

    if (startsWith(b, [0x47, 0x49, 0x46, 0x38])) return 'gif';
    if (startsWith(b, [0x42, 0x4d])) return 'bmp';
    if (startsWith(b, [0x49, 0x49, 0x2a, 0x00]) || startsWith(b, [0x4d, 0x4d, 0x00, 0x2a])) return 'tiff';
    if (startsWith(b, [0x3c, 0x3f, 0x78, 0x6d, 0x6c]) || startsWith(b, [0x3c, 0x73, 0x76, 0x67])) return 'svg';

    return null;
};

export const isSupportedFileType = (type: DetectedFileType | null): type is SupportedFileType =>
    null !== type && SUPPORTED.includes(type as SupportedFileType);

export const getFileExtension = (fileName: string) => (fileName.split('.').pop() ?? '').toLowerCase();

/** The content each accepted extension must actually contain. */
const EXTENSION_CONTENT: Record<string, SupportedFileType> = {
    jpg: 'jpg',
    jpeg: 'jpg',
    png: 'png',
    pdf: 'pdf',
};

/** Extensions that legitimately carry each content type. */
const CONTENT_EXTENSIONS: Record<SupportedFileType, string[]> = {
    jpg: ['jpg', 'jpeg'],
    png: ['png'],
    pdf: ['pdf'],
};

export type FileValidationResult =
    | { valid: true }
    | { valid: false; messageKey: string; acceptedKey: string; actual?: string; ext?: string; expected?: string };

/**
 * Builds the user-facing message. Kept here so every upload field words these the
 * same way, and so the wording can stay aware of what THAT field accepts.
 */
export const describeUploadError = (
    result: Extract<FileValidationResult, { valid: false }>,
    t: (key: string, options?: Record<string, unknown>) => string,
): string =>
    t(result.messageKey, {
        accepted: t(result.acceptedKey),
        actual: result.actual,
        ext: result.ext,
        expected: result.expected,
    });

/**
 * Mirrors the server's checks so the user cannot get past this screen with a file
 * the API would reject. This must stay AT LEAST as strict as BlobStorageHelper —
 * anything it lets through that the server refuses reintroduces the post-payment
 * failure this guard exists to prevent.
 */
export const validateUploadFile = async (file: File, allowed = ALLOWED_UPLOAD_EXTENSIONS): Promise<FileValidationResult> => {
    const ext = getFileExtension(file.name);

    // Names the formats THIS field takes, so a PDF-only field never advises "rename it to .jpg".
    const acceptedKey = allowed.some(e => ['jpg', 'jpeg', 'png'].includes(e))
        ? 'fileUpload.formats.imageOrPdf'
        : 'fileUpload.formats.pdf';

    // Mirrors gate 1 — keeps .jfif and friends out, which the server rejects by name.
    if (!allowed.includes(ext)) {
        return { valid: false, messageKey: 'fileUpload.unsupportedExtension', acceptedKey };
    }

    let detected: DetectedFileType | null;

    try {
        detected = await sniffFileType(file);
    } catch {
        // Couldn't read the file locally. Fail open here on purpose: this check is a UX
        // guard, not a control — the server re-validates every upload regardless.
        return { valid: true };
    }

    if (null === detected) {
        return { valid: false, messageKey: 'fileUpload.unrecognized', acceptedKey };
    }

    // Either an unsupported format entirely (WebP/HEIC), or a real format this
    // particular field doesn't take (a JPG on the PDF-only agreement field).
    // Renaming can't help in either case, so ask for a supported file instead.
    const usableHere = isSupportedFileType(detected) && CONTENT_EXTENSIONS[detected].some(e => allowed.includes(e));

    if (!usableHere) {
        return { valid: false, messageKey: 'fileUpload.wrongType', acceptedKey, actual: detected.toUpperCase() };
    }

    // Mirrors gate 2 — the content is fine for this field but the name disagrees, so
    // renaming genuinely does fix it. Caught HERE rather than after payment.
    if (EXTENSION_CONTENT[ext] !== detected) {
        return {
            valid: false,
            messageKey: 'fileUpload.extensionMismatch',
            acceptedKey,
            actual: detected.toUpperCase(),
            ext,
            expected: detected,
        };
    }

    return { valid: true };
};
